import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { assertNever, FeedbackRating, type TelemetryRunDetailDto } from '@signalvote/shared';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import { useAssistantTelemetryRun } from '@/hooks/queries';
import { useCampaignStore } from '@/stores';
import { conversationRows, formatCost, formatCount, formatInstant, formatMs, type ConversationRow, type ToolStatus } from './telemetryDisplay';

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground tabular-nums break-all">{children}</dd>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      {children}
    </section>
  );
}

const TOOL_STATUS_CLASS: Record<ToolStatus, string> = {
  ok: 'text-muted-foreground',
  error: 'text-destructive',
  'no result': 'text-muted-foreground italic',
};

function ConversationLine({ row }: { row: ConversationRow }) {
  switch (row.kind) {
    case 'text':
      return (
        <div className={row.role === 'user' ? 'rounded-md bg-muted px-3 py-2' : 'px-3 py-2'}>
          <div className="text-xs text-muted-foreground">{row.role === 'user' ? 'User' : 'Assistant'}</div>
          <p className="whitespace-pre-wrap text-sm text-foreground">{row.text}</p>
        </div>
      );
    case 'tool':
      return (
        <div className={`px-3 font-mono text-xs ${TOOL_STATUS_CLASS[row.status]}`}>
          {row.name} → {row.status}
        </div>
      );
    case 'moment':
      return (
        <div className="rounded-md border border-border px-3 py-2">
          <div className="text-xs text-muted-foreground">Moment · {row.momentKind}</div>
          <p className="whitespace-pre-wrap text-sm text-foreground">{row.text ?? 'Not rendered.'}</p>
        </div>
      );
    default:
      return assertNever(row);
  }
}

function RunDetail({ detail }: { detail: TelemetryRunDetailDto }) {
  const { run, metrics, reads, messages, feedback } = detail;
  const timeZoneId = useCampaignStore((s) => s.campaigns.find((c) => c.id === run.campaignId)?.timeZoneId);
  const rows = conversationRows(messages);

  return (
    <>
      <div>
        <h1 className="text-2xl font-semibold text-foreground">
          {run.trigger} run · {run.campaignName}
        </h1>
        <p className="text-muted-foreground mt-1">
          {run.userName ?? run.userEmail ?? run.userId} · started {formatInstant(run.startedAt, timeZoneId)}
        </p>
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
        <Fact label="Outcome">{run.outcome ?? 'Open'}</Fact>
        <Fact label="Ended">{formatInstant(run.endedAt, timeZoneId)}</Fact>
        <Fact label="Model">{metrics?.model ?? '—'}</Fact>
        <Fact label="Finish reason">{metrics?.finishReason ?? '—'}</Fact>
        <Fact label="Error class">{metrics?.errorClass ?? '—'}</Fact>
        <Fact label="Steps">{formatCount(metrics?.steps)}</Fact>
        <Fact label="Tool calls">{formatCount(metrics?.tools?.length)}</Fact>
        <Fact label="Cost (est.)">{formatCost(metrics?.estimatedCostUsd)}</Fact>
        <Fact label="First text server / client">
          {formatMs(metrics?.firstTextMs)} / {formatMs(run.clientFirstTextMs)}
        </Fact>
        <Fact label="Done server / client">
          {formatMs(metrics?.totalMs)} / {formatMs(run.clientDoneMs)}
        </Fact>
        <Fact label="Tokens in / out">
          {formatCount(metrics?.inputTokens)} / {formatCount(metrics?.outputTokens)}
        </Fact>
        <Fact label="Cached / reasoning tokens">
          {formatCount(metrics?.cachedInputTokens)} / {formatCount(metrics?.reasoningTokens)}
        </Fact>
      </dl>

      {metrics?.tools && metrics.tools.length > 0 && (
        <Section title="Tool calls">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tool</TableHead>
                <TableHead className="text-right">Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {metrics.tools.map((tool, i) => (
                <TableRow key={i}>
                  <TableCell className="font-mono text-xs">{tool.name}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatMs(tool.ms)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      )}

      <Section title="API reads">
        {reads.length === 0 ? (
          <p className="text-sm text-muted-foreground">No reads.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Path</TableHead>
                <TableHead className="text-right">Status</TableHead>
                <TableHead className="text-right">Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reads.map((read, i) => (
                <TableRow key={i}>
                  <TableCell className="font-mono text-xs break-all">{read.pathAndQuery}</TableCell>
                  <TableCell className={`text-right tabular-nums ${read.status >= 400 ? 'text-destructive' : ''}`}>
                    {read.status}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatMs(read.ms)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <Section title="Conversation">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No messages.</p>
        ) : (
          <div className="space-y-1">
            {rows.map((row) => (
              <ConversationLine key={row.key} row={row} />
            ))}
          </div>
        )}
      </Section>

      <Section title="Feedback">
        {feedback.length === 0 ? (
          <p className="text-sm text-muted-foreground">No feedback.</p>
        ) : (
          <ul className="space-y-2">
            {feedback.map((f) => (
              <li key={`${f.messageId}:${f.userName}`} className="flex items-baseline gap-2 text-sm">
                <Badge variant={f.rating === FeedbackRating.Up ? 'secondary' : 'destructive'}>{f.rating}</Badge>
                <span className="text-foreground">{f.reason ?? 'No reason given.'}</span>
                <span className="text-xs text-muted-foreground">
                  {f.userName ?? 'Unknown user'} · {formatInstant(f.updatedAt, timeZoneId)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  );
}

export function AssistantTelemetryRun({ runId }: { runId: string }) {
  const { data, isLoading, isError } = useAssistantTelemetryRun(runId);

  return (
    <div className="p-6 space-y-6">
      <Link
        to="/admin/assistant-telemetry"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Assistant telemetry
      </Link>
      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading run…
        </div>
      ) : isError || !data ? (
        <div className="text-sm text-destructive">Could not load this run.</div>
      ) : (
        <RunDetail detail={data} />
      )}
    </div>
  );
}
